import router from '@/router'
import { CognitoUser, CognitoUserAttribute, CognitoUserPool } from 'amazon-cognito-identity-js'
import { languageList, genderList, underrepresentedList } from '@/util/SelectLists'

const jwtDecode = require('jwt-decode')

const userPool = new CognitoUserPool({
  UserPoolId: process.env.AWS_USER_POOL_ID,
  ClientId: process.env.AWS_USER_POOL_CLIENT
})

function HEXID () {
  // Returns a random 5-digit hex hash
  return Math.floor((1 + Math.random()) * 0x100000).toString(16).substring(1)
};

const attributeNames = {
  id: 'id',
  given_name: 'firstName',
  family_name: 'lastName',
  nickname: 'nickname',
  address: 'address',
  birthdate: 'birthdate',
  gender: 'gender',
  gender_identity: 'genderIdentity',
  underrepresented: 'underrepresented',
  underrepresented_group: 'underrepresentedGroup',
  disability: 'disability',
  disability_type: 'disabilityType',
  phone_number: 'phoneNumber',
  sv_id: 'svid',
  can_call: 'canCall',
  can_text: 'canText',
  languages: 'languages',
  email: 'email'
}

const initState = () => ({
  isAuthenticated: false,
  confirmRegistration: false,
  forgotPassword: false,
  changedPassword: false,
  passwordResetToken: '',
  loadingAttributes: false,
  loadedAttributes: false,
  isAdmin: false,
  isTeamLeader: false,
  session: {
    accessToken: null
  },
  profile: {
    id: 0,
    svid: '',
    email: '',
    firstName: '',
    lastName: '',
    nickname: '',
    birthdate: '',
    gender: [],
    genderIdentity: '',
    underrepresented: [],
    underrepresentedGroup: '',
    disability: '',
    disabilityType: '',
    address: {
      address1: '',
      address2: '',
      city: '',
      region: '',
      country: '',
      postalCode: ''
    },
    languages: [],
    phoneNumber: '',
    canText: '',
    canCall: ''
  }
})

const user = {
  namespaced: true,
  state: initState(),
  mutations: {
    setSession (state, accessToken) {
      state.session.accessToken = accessToken
    },
    logout (state) {
      const init = initState()
      Object.keys(init).forEach(key => { state[key] = init[key] })
    },
    authenticateSuccess (state, { user, session }) {
      state.isAuthenticated = true
      state.isVerified = true
      state.session.accessToken = session.access_token
      Object.keys(attributeNames).forEach(att => {
        if (att === 'address') {
          if (user.address !== null) {
            // Parse JSON string
            state.profile.address = user[att]
          }
        } else if (att === 'languages' && user.languages !== null) {
          if (user.languages !== '') {
            // Split comma separated list and map them to language options
            let lang = user.languages.split(',')
            lang = lang.map(v => {
              return languageList.find(o => {
                return o.value === v
              })
            })
            state.profile.languages = lang
          } else {
            state.profile.languages = []
          }
        } else if (att === 'gender' && user.gender !== null) {
          if (user.gender !== '') {
            // Split comma separated list and map them to gender options
            let gender = user.gender.split(/\{?"(,")?\}?/)
            gender = gender.filter(v => {
              return genderList.find(o => {
                return o.value === v
              })
            })
            state.profile.gender = gender
          } else {
            state.profile.gender = []
          }
        } else if (att === 'underrepresented' && user.underrepresented !== null) {
          if (user.underrepresented !== '') {
            // Split comma separated list and map them to race options
            let underrepresented = user.underrepresented.split(/\{?"(,")?\}?/)
            underrepresented = underrepresented.filter(v => {
              return underrepresentedList.find(o => {
                return o.value === v
              })
            })
            state.profile.underrepresented = underrepresented
          } else {
            state.profile.underrepresented = []
          }
        } else {
          state.profile[attributeNames[att]] = user[att]
        }
      })
      state.loadedAttributes = true
      let token = jwtDecode(session.access_token)
      if (token.rol === 'Team-Leader') {
        state.isTeamLeader = true
      }
    },
    authenticateFailure (state) {
      state.isAuthenticated = false
    },
    registerSuccess (state, { user }) {
      state.username = user.username
      state.session = user.session
      state.isVerified = user.userConfirmed
      state.confirmRegistration = true
    },
    setForgottenPasswordToken (state, token) {
      state.passwordResetToken = token
      state.forgotPassword = true
    },
    changedPassword (state) {
      state.passwordResetToken = ''
      state.forgotPassword = false
    },
    confirmRegistration (state) {
      state.confirmRegistration = false
      state.isVerified = true
    },
    verifySuccess (state) {
      state.isVerified = true
    },
    updateProfile (state, { name, value }) {
      state.profile[name] = value
    },
    updateProfileAddress (state, { name, value }) {
      state.profile.address[name] = value
    },
    updateProfileEmergencyContact (state, { name, value }) {
      state.profile.emergencyContact[name] = value
    },
    loadProfile (state, attributes) {
      Object.keys(attributes).forEach(att => {
        if (typeof attributes[att] === 'object') {
          Object.keys(attributes[att]).forEach(subAtt => {
            state.profile[att][subAtt] = attributes[att][subAtt]
          })
        }
        state.profile[att] = attributes[att]
      })
      state.loadingAttributes = false
      state.loadedAttributes = true
    },
    isLoadingProfile (state) {
      state.loadingAttributes = true
    },
    needsVerification (state) {
      state.isVerified = false
    },
    forgotPassword (state, email) {
      state.forgotPassword = true
      state.profile.email = email
    }
  },
  getters: {
    getGenders: (state) => {
      return state.profile.gender
    },
    getEthnicity: (state) => {
      return state.profile.underrepresented
    },
    getLanguages: (state) => {
      return state.profile.languages
    },
    isConfirmedSv: (state, getters, rootState, rootGetters) => {
      return rootGetters.isAfterDeadline && rootState.application.statusId === 5
    },
    profileIncomplete: (state) => {
      if (!state.profile.birthdate ||
      !state.profile.gender ||
      !state.profile.underrepresented ||
      !state.profile.disability ||
      !state.profile.firstName ||
      !state.profile.lastName ||
      !state.profile.phoneNumber ||
      !state.profile.address.address1 ||
      !state.profile.address.city ||
      !state.profile.address.region ||
      !state.profile.address.country ||
      !state.profile.address.postalCode) {
        return true
      }
      return false
    }
  },
  actions: {
    login ({ commit }, { email, password }) {
      return new Promise((resolve, reject) => {
        fetch(process.env.BASE_URL + 'users/login', {
          method: 'POST',
          body: JSON.stringify({
            email: email,
            password: password
          })
        }).then(response => {
          response.json().then(payload => {
            if (payload.success) {
              commit('authenticateSuccess', payload.data)
              localStorage.setItem('accessToken', payload.data.session.access_token)
              router.push('/dashboard')
              resolve()
            } else {
              commit('authenticateFailure')
              commit('error', payload.data, { root: true })
              reject(payload.data.err)
            }
          })
        }).catch(err => {
          commit('authenticateFailure')
          commit('error', err, { root: true })
          reject(err)
        })
      })
    },
    logout ({ commit }, verify = false) {
      commit('logout')
      commit('application/appClearData', null, { root: true })
      commit('schedule/clearSchedule', null, { root: true })
      localStorage.removeItem('accessToken')
      router.push('/')
    },
    register ({ commit, dispatch }, { firstName, lastName, email, password }) {
      return new Promise((resolve, reject) => {
        fetch(process.env.BASE_URL + 'users/register', {
          method: 'POST',
          body: JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            email: email,
            password: password,
            svid: HEXID()
          })
        }).then(response => {
          response.json().then(payload => {
            if (payload.success) {
              // Now we attempt to login the newly registered user
              dispatch('login', { email, password })
              resolve()
            } else {
              if (payload.data.error.code === 'EMESSAGE') {
                // User is created, but email was not able to be sent
                dispatch('login', { email, password })
                resolve()
              } else {
                commit('error', payload.data, { root: true })
                reject(payload.data.err)
              }
            }
          })
        }).catch(err => {
          commit('error', err, { root: true })
          reject(err)
        })
      })
    },
    forgotPassword ({ commit }, email) {
      return new Promise((resolve, reject) => {
        fetch(process.env.BASE_URL + 'users/forgot-password', {
          method: 'POST',
          body: JSON.stringify({email: email})
        }).then(response => {
          response.json().then(payload => {
            if (payload.success) {
              commit('message', { message: 'Check your email for a link to reset your password.' }, { root: true })
              resolve()
            } else {
              commit('error', payload.data, { root: true })
              reject(payload.data.err)
            }
          })
        }).catch(err => {
          commit('error', err, { root: true })
          reject(err)
        })
      })
    },
    changePassword ({ commit, state }, { password, retypePassword }) {
      return new Promise((resolve, reject) => {
        if (password !== retypePassword) {
          let error = { message: 'Passwords do not match', error: {} }
          commit('error', error, { root: true })
          reject(error)
        }

        fetch(process.env.BASE_URL + 'users/change-password', {
          method: 'POST',
          body: JSON.stringify({ token: state.passwordResetToken, password: password })
        }).then(response => {
          response.json().then(payload => {
            if (payload.success) {
              commit('message', { message: 'Successfully changed password' }, { root: true })
              commit('changedPassword')
              resolve()
            } else {
              commit('error', payload.data, { root: true })
              reject(payload.data.err)
            }
          })
        }).catch(err => {
          console.log(err)
          commit('error', err, { root: true })
          reject(err)
        })
      })
    },
    loadAttributes ({ commit, state }) {
      commit('isLoadingProfile')
      return new Promise((resolve, reject) => {
        fetch(process.env.BASE_URL + 'users/profile', {
          headers: {
            'Authorization': state.session.accessToken
          }
        }).then(response => {
          response.json().then(payload => {
            if (payload.success) {
              commit('authenticateSuccess', payload.data)
              resolve()
            } else {
              commit('error', payload.data, { root: true })
              reject(payload.data.err)
            }
          })
        }).catch(err => {
          console.log(err)
          commit('error', err, { root: true })
          reject(err)
        })
      })
    },
    saveAttributes ({ commit, state }) {
      return new Promise((resolve, reject) => {
        fetch(process.env.BASE_URL + 'users/profile', {
          method: 'POST',
          headers: {
            'Authorization': state.session.accessToken
          },
          body: JSON.stringify(state.profile)
        }).then(response => {
          response.json().then(payload => {
            if (payload.success) {
              commit('message', { message: 'Profile Updated' }, { root: true })
              resolve()
            } else {
              commit('error', payload.data, { root: true })
              reject(payload.data.err)
            }
          })
        }).catch(err => {
          commit('error', err, { root: true })
          reject(err)
        })
      })
    },
    updateEmail ({ commit, state, dispatch }, email) {
      let cognitoUser = userPool.getCurrentUser()
      cognitoUser.signInUserSession = state.session
      cognitoUser.updateAttributes([new CognitoUserAttribute({
        Name: 'email',
        Value: email
      })], (err, result) => {
        if (err) {
          commit('error', err, { root: true })
        } else {
          dispatch('logout', true)
        }
      })
    },
    sendForgotPasswordEmail ({ commit }, email) {
      return new Promise((resolve, reject) => {
        let cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool
        })
        cognitoUser.forgotPassword({
          onSuccess: (data) => {
            commit('forgotPassword', email)
            resolve()
          },
          onFailure: (err) => {
            commit('error', err, { root: true })
            reject(err)
          }
        })
      })
    },
    confirmRegistration ({ commit, state, dispatch }, { email, password, verificationCode }) {
      let cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      })
      cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
        if (err) {
          commit('error', err, { root: true })
        } else {
          commit('confirmRegistration')
          dispatch('login', { email, password })
        }
      })
    },
    submitConfirmation ({ commit, state }, { data }) {
      // Add profile data to confirmation submission
      data.sv_id = state.profile.id
      return new Promise((resolve, reject) => {
        fetch(process.env.BASE_URL + 'users/confirm',
          {
            method: 'POST',
            headers: {
              'Authorization': state.session.accessToken
            },
            body: JSON.stringify(data)
          })
          .then(response => {
            return response.json().then(payload => {
              if (payload.success) {
                resolve()
              } else {
                commit('error', payload.err, { root: true })
                reject(payload.err)
              }
            })
          })
      })
    },
    declineAcceptance ({ commit, state }) {
      return new Promise((resolve, reject) => {
        fetch(process.env.BASE_URL + 'users/decline',
          {
            method: 'POST',
            headers: {
              'Authorization': state.session.accessToken
            },
            body: JSON.stringify({ sv_id: state.profile.id })
          })
          .then(response => {
            return response.json().then(payload => {
              if (payload.success) {
                resolve()
              } else {
                commit('error', payload.err, { root: true })
                reject(payload.err)
              }
            })
          })
      })
    }
  }
}

export default user



// WEBPACK FOOTER //
// ./src/store/user.js